import { Component } from "@theme/component";
import {
  ThemeEvents,
  VariantUpdateEvent,
  ZoomMediaSelectedEvent,
} from "@theme/events";

const volImages = {
  "2ml":
    "https://cdn.shopify.com/s/files/1/0982/7329/6732/files/atomizer-2ml.png?v=1768779788",
  "3ml":
    "https://cdn.shopify.com/s/files/1/0982/7329/6732/files/atomizer-3ml.png?v=1768779833",
  "5ml":
    "https://cdn.shopify.com/s/files/1/0982/7329/6732/files/atomizer-5ml.png?v=1768779767",
  "6ml":
    "https://cdn.shopify.com/s/files/1/0982/7329/6732/files/atomizer-6ml.png?v=1768779550",
  "10ml":
    "https://cdn.shopify.com/s/files/1/0982/7329/6732/files/atomizer-10ml.png?v=1768779727",
  "12ml":
    "https://cdn.shopify.com/s/files/1/0982/7329/6732/files/atomizer-12ml.png?v=1768779747",
  "20ml":
    "https://cdn.shopify.com/s/files/1/0982/7329/6732/files/atomizer-20ml.png?v=1768779601",
  "30ml":
    "https://cdn.shopify.com/s/files/1/0982/7329/6732/files/atomizer-30ml.jpg?v=1768779465",
  "50ml":
    "https://cdn.shopify.com/s/files/1/0982/7329/6732/files/atomizer-50ml.jpg?v=1768779426",
  "100ml":
    "https://cdn.shopify.com/s/files/1/0982/7329/6732/files/atomizer-100ml.jpg?v=1768779396",
};

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.isVariantUpdate === "true") {
      this.#updateVolumeSlide();
    }

    const { signal } = this.#controller;
    const target = this.closest(".shopify-section, dialog");

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal },
    );
    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      {
        signal,
      },
    );

    this.#preloadGalleryImages();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    // console.log("MediaGallery: Variant update received", event.detail);

    if (!source) return;
    const newMediaGallery = source.querySelector("media-gallery");

    if (!(newMediaGallery instanceof HTMLElement)) {
      console.warn("MediaGallery: New media-gallery not found in response");
      return;
    }

    newMediaGallery.dataset.isVariantUpdate = "true";
    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  #preloadGalleryImages() {
    const schedule =
      window.requestIdleCallback?.bind(window) ??
      ((callback) => window.setTimeout(callback, 1500));

    schedule(() => {
      const urls = new Set();
      const images = this.querySelectorAll("img[data_preload_src]");

      images.forEach((img) => {
        if (!(img instanceof HTMLImageElement)) return;
        const preloadSrc = img.getAttribute("data_preload_src");
        if (preloadSrc) urls.add(preloadSrc);
      });

      let count = 0;
      urls.forEach((url) => {
        if (count >= 12) return;
        const image = new Image();
        image.decoding = "async";
        image.src = url;
        count += 1;
      });
    });
  }

  #updateVolumeSlide() {
    requestAnimationFrame(() => {
      const variantTitle = this.dataset.variantTitle;
      // console.log("MediaGallery: Updating for variant", variantTitle);
      if (!variantTitle) return;

      const volumeMatch = variantTitle.match(/(\d+ml)/i);
      const volume = volumeMatch ? volumeMatch[0].toLowerCase() : null;
      const imageUrl =
        volume && volume in volImages
          ? volImages[/** @type {keyof typeof volImages} */ (volume)]
          : null;

      // console.log("MediaGallery: Volume found", volume, "Image URL", imageUrl);

      if (!imageUrl) return;

      // Helper to update img src/srcset/high-res
      const updateImg = (img) => {
        if (!img) return;
        img.src = imageUrl;
        img.srcset = "";
        img.setAttribute("data_max_resolution", imageUrl);
      };

      // 1. Update main slideshow first image
      const slideshow = this.refs.slideshow;
      if (slideshow) {
        // slideshow.refs.slides is an array if ref="slides[]" is used
        const firstMainSlide =
          slideshow.refs.slides?.[0] || slideshow.querySelector("slideshow-slide");
        updateImg(firstMainSlide?.querySelector("img"));

        // 2. Update main slideshow thumbnails (if any)
        // slideshow-controls.liquid uses ref="thumbnails[]"
        const mainThumbnails = slideshow.refs.thumbnails;
        if (Array.isArray(mainThumbnails)) {
          // It's an array of scroll-hints? No, usually buttons inside scroll-hint.
          // Let's check both possibilities.
          mainThumbnails.forEach((container) => {
            const firstThumb = container.querySelector("button, img");
            updateImg(firstThumb instanceof HTMLImageElement ? firstThumb : firstThumb?.querySelector("img"));
          });
        }
      }

      // 3. Update zoom dialog first image and its thumbnails
      const zoomDialog = this.refs.zoomDialogComponent;
      if (zoomDialog) {
        const zoomScroller = zoomDialog.querySelector(".dialog-zoomed-gallery");
        const firstZoomSlide = zoomScroller?.querySelectorAll("li")[0];
        updateImg(firstZoomSlide?.querySelector("img"));

        const zoomThumbnails = zoomDialog.refs.thumbnails;
        if (zoomThumbnails) {
          const firstZoomThumbBtn = zoomThumbnails.querySelector("button");
          updateImg(firstZoomThumbBtn?.querySelector("img"));
        }
      }
    });
  }
}

if (!customElements.get("media-gallery")) {
  customElements.define("media-gallery", MediaGallery);
}
